/**
 * Products tab — Settings → Product & Services.
 *
 * Shows products grouped by product_group_name using DataTable's getSubRows
 * tree mode. Parent rows (depth 0) show group name + product count + addon
 * count. Child rows (depth 1) show the individual product fields.
 *
 * Matches legacy ProductsTab/ExpandedProductGroup column set:
 * Group level: Name | Products | Add-ons
 * Product level: ID | Code | Name | Description | Price Model | Price | Status | Created
 *
 * Search filters group names and product names client-side (full dataset
 * fetched once from /products/search?page_size=500).
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Package } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { formatDate, formatMoney } from '@/lib/format';
import { useAdminProducts, type ProductSearchRow } from '@/api/products-admin';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Unified tree node: group row wraps product rows as sub-rows. */
interface ProductTreeNode {
  _type: 'group' | 'product';
  // Group fields (populated at depth 0)
  product_group_id?: number;
  product_group_name?: string;
  _productCount?: number;
  _addonCount?: number;
  subRows?: ProductTreeNode[];
  // Product fields (populated at depth 1)
  product_id?: number;
  product_name?: string;
  product_code?: string | null;
  product_hidden?: boolean;
  product_description?: string | null;
  product_price_model?: string | null;
  current_price?: number | null;
  created_at?: string | null;
  is_product_addon?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function buildTree(products: ProductSearchRow[], filter: string): ProductTreeNode[] {
  const q = filter.toLowerCase();
  const filtered = q
    ? products.filter(
        (p) =>
          p.product_name.toLowerCase().includes(q) ||
          (p.product_code ?? '').toLowerCase().includes(q) ||
          p.product_group_name.toLowerCase().includes(q),
      )
    : products;

  const map = new Map<number, ProductTreeNode>();
  for (const p of filtered) {
    const gid = p.product_group_id;
    if (!map.has(gid)) {
      map.set(gid, {
        _type: 'group',
        product_group_id: gid,
        product_group_name: p.product_group_name,
        _productCount: 0,
        _addonCount: 0,
        subRows: [],
      });
    }
    const group = map.get(gid)!;
    if (p.is_product_addon) group._addonCount! += 1;
    else group._productCount! += 1;
    group.subRows!.push({
      _type: 'product',
      product_id: p.product_id,
      product_name: p.product_name,
      product_code: p.product_code,
      product_hidden: p.product_hidden,
      product_description: p.product_description,
      product_price_model: p.product_price_model,
      current_price: p.current_price,
      created_at: p.created_at,
      is_product_addon: p.is_product_addon,
    });
  }
  return Array.from(map.values()).sort((a, b) =>
    (a.product_group_name ?? '').localeCompare(b.product_group_name ?? ''),
  );
}

function priceModelLabel(model: string | null | undefined): string {
  switch (model) {
    case 'flexible': return 'Flexible';
    case 'package': return 'Package';
    case 'bundle': return 'Bundle';
    case 'percent_of_parent': return '% Parent';
    default: return model ?? 'Flexible';
  }
}

function priceModelTone(model: string | null | undefined): 'success' | 'info' | 'warning' | 'neutral' {
  switch (model) {
    case 'flexible': return 'success';
    case 'percent_of_parent': return 'warning';
    case 'package':
    case 'bundle': return 'info';
    default: return 'neutral';
  }
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const COLUMNS: ColumnDef<ProductTreeNode, unknown>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') {
        return (
          <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>
            {node.product_group_name}
          </span>
        );
      }
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          {node.is_product_addon && <StatusPill tone="info">Add-on</StatusPill>}
          {node.product_name}
        </span>
      );
    },
  },
  {
    id: 'code',
    header: 'Code / Count',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') {
        return (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {node._productCount} products · {node._addonCount} add-ons
          </span>
        );
      }
      return (
        <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {node.product_code || '—'}
        </span>
      );
    },
  },
  {
    id: 'price_model',
    header: 'Price Model',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return (
        <StatusPill tone={priceModelTone(node.product_price_model)}>
          {priceModelLabel(node.product_price_model)}
        </StatusPill>
      );
    },
  },
  {
    id: 'current_price',
    header: 'Current Price',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(node.current_price)}
        </span>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return node.product_hidden ? (
        <StatusPill tone="warning">Hidden</StatusPill>
      ) : (
        <StatusPill tone="success">Active</StatusPill>
      );
    },
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return formatDate(node.created_at);
    },
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function ProductsTab() {
  const { data, isLoading, isError, refetch } = useAdminProducts({
    page: 1,
    page_size: 500,
  });

  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 300);

  const treeData = useMemo(
    () => buildTree(data?.results ?? [], query),
    [data?.results, query],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Search products…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load products"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={treeData}
          getRowId={(row) =>
            row._type === 'group'
              ? `g-${row.product_group_id}`
              : `p-${row.product_id}`
          }
          getSubRows={(row) => row.subRows}
          defaultAllExpanded={false}
          rowClassName={(_row, depth) =>
            depth === 0 ? 'product-group-row' : ''
          }
          emptyState={
            <EmptyState
              icon={<Package size={24} />}
              title="No products found"
              description="Try adjusting your search."
            />
          }
        />
      )}
    </div>
  );
}
